/**
 * Asking the search route what a reader is part-way through typing.
 *
 * **This used to be a file.** The build published one list per writing and a
 * box fetched the whole of it on the first keystroke, which was 175 KB brotli
 * over the section as it stands and 927 KB over a full CC-CEDICT.
 * `docs/search.md` under "Suggesting words as a reader types" has the argument
 * for the route answering instead, and the short of it is that what a reader
 * downloads no longer tracks the size of the section.
 *
 * **Two things keep the requests down and neither is the debounce.** A prefix
 * answered once is answered off this page for as long as it is open, so a
 * reader backspacing over a word and typing it again pays for none of it. And
 * the route's answers carry `max-age=300`, so the prefixes a reader types on
 * one page are already in the browser's cache on the next. The debounce in
 * `./search-suggestions.ts` is what keeps a burst of keystrokes from asking
 * for the prefixes nobody stopped at.
 *
 * **Every failure here is the page as it was.** A refused request, a network
 * that never answers and a body this cannot read all end in an empty list,
 * which is the box and the button beside it. That covers the rate limit too:
 * a reader who somehow trips `src/firewall/search-rate-rule.ts` loses the
 * shortcut and keeps the search.
 */

import {
  type CidianSuggestion,
  readCidianSuggestions,
} from "../content/cidian/cidian-suggestion-line.js";
import {
  type SuggestionWriting,
  suggestionAddress,
} from "../site-search/search-paths.js";

/**
 * How long a request is given before it is abandoned.
 *
 * A suggestion is worth having while the reader is still typing that prefix and
 * worthless afterwards, so five seconds is far past the point of drawing one.
 * What it really bounds is a request that never answers at all. Without it the
 * promise stays in `#asking` and that prefix is silent for as long as the page
 * is open, which is the one failure the rule below does not cover.
 */
const longestWait = 5000;

/** What a page has already asked for, so it asks for none of it twice. */
function held(writing: SuggestionWriting, query: string): string {
  return `${writing}\t${query}`;
}

/**
 * The route, as the boxes on one page share it.
 *
 * One of these per page rather than one per box. The rail draws a box on every
 * 词典 and 例句 page and the article draws another, and a reader who types the
 * same prefix into each is answered from here the second time.
 */
export class RouteSuggestions {
  readonly #answered = new Map<string, readonly CidianSuggestion[]>();
  readonly #asking = new Map<string, Promise<readonly CidianSuggestion[]>>();

  /**
   * What this page already knows about a prefix, without asking anything.
   *
   * Read before the wait, so a prefix that has been answered is drawn on the
   * keystroke rather than after it. That is most of what a reader backspacing
   * sees.
   */
  offered(
    writing: SuggestionWriting,
    query: string,
  ): readonly CidianSuggestion[] | undefined {
    return this.#answered.get(held(writing, query));
  }

  /** The words for a prefix, asked for once however many boxes want them. */
  async offer(
    writing: SuggestionWriting,
    query: string,
  ): Promise<readonly CidianSuggestion[]> {
    const key = held(writing, query);
    const asking = this.#asking.get(key) ?? this.#ask(key, writing, query);

    this.#asking.set(key, asking);

    return asking;
  }

  /**
   * One request, remembered by its answer.
   *
   * **A failed request is forgotten**, an abandoned one included. What a
   * failure means is a reader offline for a moment, a rate limit reached or a
   * connection that has stopped answering, and all three pass. A remembered
   * empty list would leave that prefix silent for as long as the page is open.
   */
  async #ask(
    key: string,
    writing: SuggestionWriting,
    query: string,
  ): Promise<readonly CidianSuggestion[]> {
    const found = await fetched(writing, query);

    if (found === undefined) {
      this.#asking.delete(key);

      return [];
    }

    this.#answered.set(key, found);

    return found;
  }
}

/**
 * One request, or nothing at all where it did not come back readable.
 *
 * **A controller and a timer of its own rather than `AbortSignal.timeout`.**
 * Both are in every browser this site is built for, and the difference is that
 * one of them can be wound on. `AbortSignal.timeout` schedules inside the
 * runtime where a test's clock cannot reach it, so the case that proves a
 * request is abandoned would sit there for five real seconds and pass whether
 * or not the abort ever fired.
 *
 * An abort arrives here as the throw any other network failure arrives as, so
 * a request that ran out of time is forgotten exactly as a refused one is and
 * the next keystroke on that prefix asks again.
 */
async function fetched(
  writing: SuggestionWriting,
  query: string,
): Promise<readonly CidianSuggestion[] | undefined> {
  const abandon = new AbortController();
  const waiting = setTimeout(() => {
    abandon.abort();
  }, longestWait);

  try {
    const response = await fetch(suggestionAddress(writing, query), {
      signal: abandon.signal,
    });

    return response.ok
      ? readCidianSuggestions(await response.text())
      : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(waiting);
  }
}
